import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AvailableCommand, CommandOption, UserSummary } from "@atrium/shared";
import { ApiError, api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { Avatar } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { BotTag } from "../../components/UserBadges";
import { IconChevronDown, IconSend, IconX } from "../../components/icons";

/*
 * Slash commands in the channel composer. Typing "/" lists the commands of the
 * community's bots; picking one switches the composer into command mode, with
 * an input per option. Sending posts an interaction instead of a message, and
 * the bot's reply arrives as a normal channel message.
 */

export interface CommandContext {
  communityId: string;
  channelId: string;
  /** Members for user options (from the member list cache). */
  members: UserSummary[];
  /** Text channels for channel options. */
  channels: { id: string; name: string }[];
}

export const commandsKey = (communityId: string) => ["commands", communityId] as const;

export function useCommands(communityId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: commandsKey(communityId ?? ""),
    queryFn: () => api.get<{ items: AvailableCommand[] }>(`/api/communities/${communityId}/commands`).then((r) => r.items),
    enabled: !!communityId && enabled,
    staleTime: 30_000,
  });
}

/** "/ro" → commands whose name starts with "ro", then those containing it. */
export function matchCommands(all: AvailableCommand[], text: string): AvailableCommand[] | null {
  const m = /^\/([\w-]*)$/.exec(text);
  if (!m) return null;
  const q = m[1].toLowerCase();
  const starts = all.filter((c) => c.name.startsWith(q));
  const contains = q ? all.filter((c) => !c.name.startsWith(q) && c.name.includes(q)) : [];
  return [...starts, ...contains];
}

const cmdId = (c: AvailableCommand) => `${c.applicationId}:${c.name}`;

export function CommandPicker({ items, index, onPick, onHover }: { items: AvailableCommand[]; index: number; onPick: (c: AvailableCommand) => void; onHover: (i: number) => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);
  return (
    <div
      ref={listRef}
      id="command-suggestions"
      className="absolute bottom-full left-4 right-4 mb-1 max-h-80 overflow-y-auto rounded-lg bg-surface p-1 shadow-pop animate-pop-in"
      role="listbox"
      aria-label="Commands"
    >
      {items.map((c, i) => (
        <button
          key={cmdId(c)}
          id={`command-option-${i}`}
          data-index={i}
          role="option"
          aria-selected={i === index}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(c);
          }}
          onMouseEnter={() => onHover(i)}
          className={cn("flex min-h-11 w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left", i === index ? "bg-surface-3" : "hover:bg-surface-2")}
        >
          <Avatar src={c.bot.avatarUrl} name={c.bot.displayName} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">
              <span className="font-mono font-medium text-fg">/{c.name}</span>
              {c.options.length > 0 && (
                <span className="ml-1.5 font-mono text-xs text-fg-3">
                  {c.options.map((o) => (o.required ? o.name : `[${o.name}]`)).join(" ")}
                </span>
              )}
            </span>
            <span className="block truncate text-xs text-fg-3">{c.description}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5 text-xs text-fg-3">
            <span className="max-w-32 truncate">{c.bot.displayName}</span>
            <BotTag />
          </span>
        </button>
      ))}
    </div>
  );
}

type Values = Record<string, string>;

/** Convert the form's strings to what the server expects for each option type. */
function toOptions(defs: CommandOption[], values: Values) {
  const out: Record<string, string | number | boolean> = {};
  for (const d of defs) {
    const raw = values[d.name]?.trim() ?? "";
    if (!raw) continue;
    if (d.type === "boolean") out[d.name] = raw === "true";
    else if (d.type === "integer" || d.type === "number") {
      const n = Number(raw);
      out[d.name] = Number.isFinite(n) ? n : raw; // let the server explain bad numbers
    } else if (d.choices?.length) {
      const choice = d.choices.find((c) => String(c.value) === raw);
      out[d.name] = choice ? choice.value : raw;
    } else out[d.name] = raw;
  }
  return out;
}

const fieldBase =
  "h-8 min-w-0 rounded-md border border-line-strong bg-bg-subtle px-2 text-sm text-fg placeholder:text-fg-3 focus:border-accent focus:outline-none aria-[invalid=true]:border-danger";

function OptionInput({
  def,
  value,
  onChange,
  error,
  ctx,
  autoFocus,
  onKeyDown,
}: {
  def: CommandOption;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  ctx: CommandContext;
  autoFocus?: boolean;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}) {
  const id = `cmd-opt-${def.name}`;
  const common = {
    id,
    "aria-invalid": !!error,
    "aria-describedby": error ? `${id}-err` : undefined,
    "aria-required": def.required,
    autoFocus,
    onKeyDown,
    title: def.description,
  };
  let control;
  const select = (options: { value: string; label: string }[]) => (
    <span className="relative inline-flex min-w-0">
      <select {...common} value={value} onChange={(e) => onChange(e.target.value)} className={cn(fieldBase, "max-w-56 appearance-none pr-7")}>
        <option value="">{def.required ? "Choose…" : "None"}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <IconChevronDown className="icon pointer-events-none absolute right-1.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
    </span>
  );
  if (def.choices?.length) control = select(def.choices.map((c) => ({ value: String(c.value), label: c.name })));
  else if (def.type === "boolean")
    control = select([
      { value: "true", label: "True" },
      { value: "false", label: "False" },
    ]);
  else if (def.type === "user" && ctx.members.length)
    control = select(ctx.members.map((m) => ({ value: m.id, label: `${m.displayName} (@${m.username})` })));
  else if (def.type === "channel" && ctx.channels.length) control = select(ctx.channels.map((c) => ({ value: c.id, label: `#${c.name}` })));
  else
    control = (
      <input
        {...common}
        type={def.type === "integer" || def.type === "number" ? "number" : "text"}
        step={def.type === "integer" ? 1 : def.type === "number" ? "any" : undefined}
        inputMode={def.type === "integer" ? "numeric" : def.type === "number" ? "decimal" : undefined}
        value={value}
        maxLength={def.type === "string" ? 1000 : undefined}
        placeholder={def.type === "user" ? "User ID" : def.type === "channel" ? "Channel ID" : def.description}
        onChange={(e) => onChange(e.target.value)}
        className={cn(fieldBase, def.type === "string" ? "w-56" : "w-28")}
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <label htmlFor={id} className="shrink-0 font-mono text-xs text-fg-2">
          {def.name}
          {def.required && (
            <span className="text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
        {control}
      </div>
      {error && (
        <span id={`${id}-err`} role="alert" className="text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}

/** Command mode: the command as a chip plus an input per option. */
export function CommandForm({ command, ctx, onExit, onSent }: { command: AvailableCommand; ctx: CommandContext; onExit: () => void; onSent: (c: AvailableCommand) => void }) {
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the first option, or the form itself so Enter / Escape work straight away.
    if (!command.options.length) formRef.current?.focus();
  }, [command]);

  const submit = async () => {
    if (pending) return;
    const missing = Object.fromEntries(command.options.filter((o) => o.required && !values[o.name]?.trim()).map((o) => [o.name, "Required"]));
    if (Object.keys(missing).length) return setErrors(missing);
    setPending(true);
    setErrors({});
    try {
      await api.post(`/api/channels/${ctx.channelId}/interactions`, { applicationId: command.applicationId, name: command.name, options: toOptions(command.options, values) });
      onSent(command);
    } catch (e) {
      if (e instanceof ApiError && e.fields && Object.keys(e.fields).length) setErrors(e.fields);
      else setErrors({ _: errorMessage(e, "Couldn't run that command") });
    } finally {
      setPending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onExit();
    } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    } else if (e.key === "Backspace" && e.currentTarget === formRef.current) onExit();
  };

  return (
    <div ref={formRef} tabIndex={-1} onKeyDown={(e) => e.target === formRef.current && onKeyDown(e)} className="flex items-start gap-1 px-1.5 py-1.5 focus:outline-none" role="group" aria-label={`/${command.name} command`}>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5 px-1.5 py-0.5">
        <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-accent-soft pl-1.5 pr-1 text-sm text-accent">
          <Avatar src={command.bot.avatarUrl} name={command.bot.displayName} size="xs" />
          <span className="font-mono font-medium">/{command.name}</span>
          <button onClick={onExit} aria-label="Leave command mode" className="rounded p-0.5 text-accent/80 hover:bg-accent-soft hover:text-accent">
            <IconX className="icon size-4" aria-hidden />
          </button>
        </span>
        {command.options.length === 0 && <span className="text-sm text-fg-3">{command.description}</span>}
        {command.options.map((o, i) => (
          <OptionInput
            key={o.name}
            def={o}
            ctx={ctx}
            value={values[o.name] ?? ""}
            error={errors[o.name]}
            autoFocus={i === 0}
            onKeyDown={onKeyDown}
            onChange={(v) => {
              setValues((x) => ({ ...x, [o.name]: v }));
              if (errors[o.name])
                setErrors((x) => {
                  const next = { ...x };
                  delete next[o.name];
                  return next;
                });
            }}
          />
        ))}
        {errors._ && (
          <span role="alert" className="basis-full text-xs text-danger">
            {errors._}
          </span>
        )}
      </div>
      <IconButton label={`Send /${command.name}`} icon={IconSend} onClick={() => void submit()} disabled={pending} className="text-fg" />
    </div>
  );
}
